import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { from, of, switchMap, type Observable } from 'rxjs';
import { getCurrentUserID } from '../../../api/currentUserID.js';
import { canManageUsers } from '../../user-management/server/access.js';
import {
  NOTIFICATION_DONE_STATES,
  NOTIFICATION_OPEN_STATES,
  UserNotification,
} from '../db/UserNotification.js';

/**
 * Every user's notifications, live — the administrator's counterpart to
 * {@link subscribeMyNotifications}. A second operation, so the personal inbox keeps the
 * property that its filter cannot be widened from the client.
 */
export const subscribeAllNotifications = registerSubscription({
  type: () => [UserNotification],
  operationName: 'subscribeAllNotifications',
  resolve: (showDone: boolean, skip: number, limit: number) => {
    return (_gqlFields: BaseQLSelectionSet<UserNotification>): Observable<UserNotification[]> => {
      const userID = getCurrentUserID();
      if (!userID) return of([]);

      // Answers `[]` rather than throwing — a session that lost its grant gets an empty list,
      // not an error screen.
      return from(canManageUsers(userID)).pipe(
        switchMap(allowed =>
          allowed
            ? watchQuery<UserNotification>(
                UserNotification._collectionName,
                { status: { $in: showDone ? NOTIFICATION_DONE_STATES : NOTIFICATION_OPEN_STATES } },
                { skip, limit, sort: { createdAt: -1 } },
              )
            : of([]),
        ),
      );
    };
  },
  params: [
    { name: 'showDone', type: () => Boolean },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});
